import { useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Camera, CreditCard, IdCard, ShieldPlus, Loader2, Upload } from "lucide-react";

const TYPES = [
  { value: "vitale", label: "Carte Vitale", icon: CreditCard },
  { value: "mutuelle", label: "Carte de mutuelle", icon: ShieldPlus },
  { value: "identite", label: "Pièce d'identité", icon: IdCard },
];

// « Scanner un document » : type → photo (caméra) ou import → OCR local → onResult(fields, docType). L'OCR propose, l'utilisateur confirme.
export function ScanDocument({ onResult, testid = "scan" }) {
  const [type, setType] = useState(null);
  const [busy, setBusy] = useState(false);
  const camRef = useRef(null);
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file || !type) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("doc_type", type);
      const { data } = await api.post("/clients/ocr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const n = Object.keys(data.fields || {}).length;
      if (data.warning) toast.warning(data.warning); else toast.success(`${n} information${n > 1 ? "s" : ""} proposée${n > 1 ? "s" : ""} — vérifiez avant d'enregistrer`);
      onResult(data.fields || {}, type);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid={`${testid}-panel`}>
      <p className="font-heading font-bold text-brand-900 flex items-center gap-2"><Camera className="w-5 h-5 text-accent-700" />Scanner un document</p>
      <div className="grid grid-cols-3 gap-2">
        {TYPES.map((t) => (
          <button key={t.value} type="button" data-testid={`${testid}-type-${t.value}`} onClick={() => setType(t.value)}
            className={`rounded-xl border-2 p-2.5 text-xs font-bold flex flex-col items-center gap-1.5 ${type === t.value ? "border-accent-500 bg-accent-50 text-accent-800" : "border-slate-200 text-slate-600"}`}>
            <t.icon className="w-6 h-6" />{t.label}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" data-testid={`${testid}-camera`} disabled={!type || busy} onClick={() => camRef.current?.click()} className="h-12 rounded-xl bg-brand-800 text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-40">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}Prendre une photo</button>
        <button type="button" data-testid={`${testid}-import`} disabled={!type || busy} onClick={() => fileRef.current?.click()} className="h-12 rounded-xl bg-white border-2 border-brand-200 text-brand-900 font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-40"><Upload className="w-5 h-5" />Importer une image</button>
      </div>
      <input ref={camRef} data-testid={`${testid}-camera-input`} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} />
      <input ref={fileRef} data-testid={`${testid}-file-input`} type="file" accept="image/*" className="hidden" onChange={onFile} />
      <p className="text-xs text-slate-400">Analyse sur nos serveurs uniquement, photo non conservée. Un document par photo : vous vérifiez chaque champ avant d'enregistrer.</p>
    </div>
  );
}
